import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { Database, Download, Upload, Shield, RefreshCw, CheckCircle, AlertCircle, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DataManagement = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.email === "shiva.mittal@kpminfra.com";

  useEffect(() => {
    if (isMasterAdmin) {
      fetchSummary();
    }
  }, [isMasterAdmin]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/data/export/summary');
      setSummary(response.data);
    } catch (error) {
      toast.error("Failed to fetch data summary");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/data/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `kpm_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await apiClient.post('/data/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data.results);
      toast.success("Data imported successfully!");
      fetchSummary(); // Refresh summary
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
    }
  };

  if (!isMasterAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 mx-auto mb-4 text-slate-400" />
        <h2 className="font-heading font-bold text-2xl mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only Master Admin can access data management features.</p>
      </div>
    );
  }

  return (
    <div data-testid="data-management-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">DATA MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Export and import your project data</p>
        </div>
        <button onClick={fetchSummary} className="btn-outline flex items-center space-x-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-8">
        <h3 className="font-medium text-blue-800 flex items-center mb-2">
          <HardDrive className="w-5 h-5 mr-2" />
          MongoDB Persistence Guide
        </h3>
        <p className="text-sm text-blue-700 mb-3">
          To keep your data persistent across deployments, use an external MongoDB service:
        </p>
        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Create a free MongoDB Atlas account at <a href="https://www.mongodb.com/cloud/atlas" target="_blank" rel="noreferrer" className="underline">mongodb.com/cloud/atlas</a></li>
          <li>Create a cluster and get your connection string</li>
          <li>Update <code className="bg-blue-100 px-1 rounded">backend/.env</code> with: <code className="bg-blue-100 px-1 rounded">MONGO_URL=mongodb+srv://user:pass@cluster.mongodb.net</code></li>
          <li>Restart the backend service</li>
        </ol>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="stat-card border-l-4 border-l-primary">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Collections</p>
            <p className="font-heading font-black text-3xl text-primary font-mono">{summary.total_collections}</p>
          </div>
          <div className="stat-card border-l-4 border-l-accent">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Documents</p>
            <p className="font-heading font-black text-3xl text-accent font-mono">{summary.total_documents}</p>
          </div>
          <div className="stat-card border-l-4 border-l-success">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Database Status</p>
            <p className="font-heading font-black text-xl text-success flex items-center">
              <CheckCircle className="w-6 h-6 mr-2" /> Connected
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Export Card */}
        <div className="stat-card">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mr-4">
              <Download className="w-6 h-6 text-success" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl">Export Data</h3>
              <p className="text-sm text-slate-600">Download all data as JSON backup</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Creates a complete backup of all your projects, employees, materials, DPRs, and more. 
            Use this to migrate data to a new deployment or as a safety backup.
          </p>
          <button 
            onClick={handleExport} 
            disabled={exporting}
            className="btn-primary w-full flex items-center justify-center space-x-2"
            data-testid="export-btn"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{exporting ? 'Exporting...' : 'Export All Data'}</span>
          </button>
        </div>

        {/* Import Card */}
        <div className="stat-card">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
              <Upload className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl">Import Data</h3>
              <p className="text-sm text-slate-600">Restore data from JSON backup</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Import data from a previously exported JSON file. Only new records will be added - 
            existing records with the same ID will be skipped to prevent duplicates.
          </p>
          <button 
            onClick={() => setImportDialogOpen(true)}
            className="btn-outline w-full flex items-center justify-center space-x-2"
            data-testid="import-btn"
          >
            <Upload className="w-4 h-4" />
            <span>Import Data</span>
          </button>
        </div>
      </div>

      {/* Collections Table */}
      {summary && (
        <div className="stat-card">
          <h3 className="font-heading font-bold text-lg mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2 text-primary" />
            Collection Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-widest">Collection</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-widest">Documents</th>
                </tr>
              </thead>
              <tbody>
                {summary.collections.map(coll => (
                  <tr key={coll.collection} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono text-sm">{coll.collection}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">{coll.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">IMPORT DATA</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3">
              <p className="text-sm text-yellow-800 flex items-start">
                <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                Only new records will be imported. Existing records with the same ID will be skipped.
              </p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                Select Export File (JSON)
              </label>
              <input
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {importResult && (
              <div className="bg-slate-50 rounded-sm p-3 text-sm">
                <p className="font-medium mb-2">Import Results:</p>
                <ul className="space-y-1 text-xs">
                  {importResult.imported?.map(r => (
                    <li key={r.collection} className="text-success">
                      ✓ {r.collection}: {r.count} imported
                    </li>
                  ))}
                  {importResult.skipped?.map(r => (
                    <li key={r.collection} className="text-slate-500">
                      ○ {r.collection}: skipped ({r.reason})
                    </li>
                  ))}
                  {importResult.errors?.map(r => (
                    <li key={r.collection} className="text-error">
                      ✗ {r.collection}: {r.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button onClick={() => setImportDialogOpen(false)} className="btn-outline">
                Cancel
              </button>
              <button 
                onClick={handleImport} 
                disabled={importing || !importFile}
                className="btn-primary flex items-center space-x-2"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span>{importing ? 'Importing...' : 'Start Import'}</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;
